import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  Chip,
  TextField,
  IconButton,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SendIcon from '@mui/icons-material/Send';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import styles from './WizardPage.module.css';
import WizardForm from 'components/Wizard/WizardForm';
import ChatHistory from 'components/Wizard/ChatHistory';
import DocumentsSidebar from 'components/Wizard/DocumentsSidebar';
import default_theme from 'assets/themes/themes';
import fastAPIBaseURL from 'utils/fastapiConfig';
import backendURL from 'utils/config';

const getUserId = () => {
  let userId = localStorage.getItem('wizard_user_id');
  if (!userId) {
    userId = 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('wizard_user_id', userId);
  }
  return userId;
};

// Helper function to make API requests to backend-fastapi
const apiRequest = async (endpoint, options = {}) => {
  const userId = getUserId();
  const url = `${fastAPIBaseURL}/api${endpoint}`;
  
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      'X-User-ID': userId,
    },
  };

  const response = await fetch(url, {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...(options.headers || {}),
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
    throw new Error(error.detail || `API error: ${response.status}`);
  }

  return response.json();
};

const WizardPage = () => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentThreadId, setCurrentThreadId] = useState(null);
  const [showForm, setShowForm] = useState(true);
  const [formData, setFormData] = useState(null);
  const [threads, setThreads] = useState([]);
  const [userId] = useState(() => getUserId());
  const [showFormContext, setShowFormContext] = useState(false);
  const [isEditingForm, setIsEditingForm] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [threadToDelete, setThreadToDelete] = useState(null);

  // Mock documents data
  const [documents] = useState([
    { id: 1, title: 'Emergency Response Manual', type: 'PDF', createdAt: 'Today', size: '2.3 MB' },
    { id: 2, title: 'Risk Assessment Guide', type: 'DOC', createdAt: 'Yesterday', size: '1.8 MB' },
    { id: 3, title: 'Policy Framework', type: 'PDF', createdAt: '2 days ago', size: '950 KB' },
    { id: 4, title: 'Past Experiences Report', type: 'PDF', createdAt: '3 days ago', size: '1.2 MB' },
  ]);

  const messagesEndRef = useRef(null);
  const navigate = useNavigate();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const loadThreads = async () => {
      try {
        const threadsData = await apiRequest('/threads', { method: 'GET' });
        setThreads(threadsData);
      } catch (error) {
        console.error('Error loading threads:', error);
      }
    };
    loadThreads();
  }, []);

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setIsLoading(true);

    const isFirstMessage = messages.length === 0;
    const hasFormContext = formData && isFirstMessage;

    const tempUserMessage = {
      id: `temp-${Date.now()}`,
      text: userMessage,
      sender: 'user',
      timestamp: new Date().toLocaleTimeString(),
      isFirstMessage: isFirstMessage,
      hasFormContext: hasFormContext
    };
    
    const newMessages = [...messages, tempUserMessage];
    
    // Add form context message right after first user message if formData exists
    if (hasFormContext) {
      const contextMessage = {
        id: `context-${Date.now()}`,
        sender: 'context',
        timestamp: new Date().toLocaleTimeString(),
        formData: formData
      };
      newMessages.push(contextMessage);
      setShowFormContext(true);
    }
    
    setMessages(newMessages);

    try {
      const controller = new AbortController();
      // Set timeout to 10 minutes (600000ms) for local LLM requests on CPU
      const timeoutId = setTimeout(() => controller.abort(), 600000);
      
      // Include form data in the request if available for RAG context
      const requestBody = {
        message: userMessage,
        user_id: userId,
        thread_id: currentThreadId || null,
        ...(formData && {
          context: {
            goals: formData.goals,
            challenges: formData.challenges,
            resources: formData.resources,
            description: formData.description,
            relatedFields: formData.relatedFields
          }
        })
      };

      // Send to chatbot.js which handles backend-fastapi communication
      // Note: chatbot is not under /api, so use base hostname
      const backendBaseURL = process.env.REACT_APP_HOSTNAME || '';
      const response = await fetch(`${backendBaseURL}/chatbot/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal,
      });
      console.log('Response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries())
      });

      clearTimeout(timeoutId);
      if (!response.ok) {
        const errorText = await response.text();
	console.error('Error response body:', errorText);
        throw new Error('Failed to get response');
      }

      const data = await response.json();
      console.log('RAW chatbot response:', {
        status: data.status,
        hasAnswer: !!data.response?.answer,
        hasSources: !!data.response?.sources,
        sourcesCount: data.response?.sources?.length,
        threadId: data.thread_id
      });
      
      const threadId = data.thread_id;
      if (!currentThreadId && threadId) {
        setCurrentThreadId(threadId);
        const updatedThreads = await apiRequest('/threads', { method: 'GET' });
        setThreads(updatedThreads);
      }
      
      const normalizedSources = (data.response.sources || []).map(source => {
        let title = source.title;
        if (!title || title === 'Untitled' || title.trim() === '') {
          title = source.category && source.category !== 'N/A' ? source.category 
                : source.region && source.region !== 'N/A' ? source.region
                : source.text ? source.text.substring(0, 50).trim()
                : 'Source';
        }
        return {
          ...source,
          title: title,
          url: source.url || source.source_url || '', 
        };
      });
      
      const botMessage = {
        id: Date.now() + 1,
        text: data.response.answer,
        sender: 'bot',
        timestamp: new Date().toLocaleTimeString(),
        sources: normalizedSources
      };
      
      // Update messages - keep context message in correct position (after first user message)
      setMessages(prev => {
        // Find existing context message and first user message
        const contextMsg = prev.find(m => m.sender === 'context');
        const firstUserMsg = prev.find(m => m.sender === 'user' && m.id !== tempUserMessage.id);
        
        // Remove temp user message and context message (we'll reinsert it)
        const filtered = prev.filter(m => m.id !== tempUserMessage.id && m.sender !== 'context');
        
        // If we have both first user message and context message, insert context right after first user
        if (firstUserMsg && contextMsg) {
          const firstUserIndex = filtered.findIndex(m => m.id === firstUserMsg.id);
          if (firstUserIndex !== -1) {
            // Insert context right after first user message, then add bot message at end
            const withContext = [...filtered];
            withContext.splice(firstUserIndex + 1, 0, contextMsg);
            return [...withContext, botMessage];
          }
        }
        
        // Fallback: if context exists but we can't find first user, add it before bot
        if (contextMsg) {
          return [...filtered, contextMsg, botMessage];
        }
        
        return [...filtered, botMessage];
      });
      
      // Reload messages from database to get proper IDs and ensure consistency
      if (threadId) {
        try {
          const messagesData = await apiRequest(`/messages/${threadId}`);
          const mappedMessages = messagesData.map((msg, index) => {
            console.log('RAW stored sources:', msg.metadata?.knowledge_used);
            return {
              id: msg.message_id,
              text: msg.content,
              sender: msg.role === 'assistant' ? 'bot' : msg.role,
              timestamp: new Date(msg.created_at).toLocaleTimeString(),
              sources: (msg.metadata?.knowledge_used || []).map(source => ({
                ...source, url: source.url || source.source_url || '', }))
            };
          });

          // const mappedMessages = messagesData.map((msg, index) => ({
          //   console.log('RAW stored sources:', msg.metadata?.knowledge_used);
          //   id: msg.message_id,
          //   text: msg.content,
          //   sender: msg.role === 'assistant' ? 'bot' : msg.role,
          //   timestamp: new Date(msg.created_at).toLocaleTimeString(),
          //   sources: (msg.metadata?.knowledge_used || []).map(source => ({
          //     ...source,
          //     url: source.url || source.source_url || '',
          //   }))
          // }));
          
          // Find first user message and check for form context
          const firstUserMessageIndex = mappedMessages.findIndex(m => m.sender === 'user');
          let contextMessage = null;
          
          if (firstUserMessageIndex !== -1) {
            const firstMsgData = messagesData[firstUserMessageIndex];
            const firstUserMessage = mappedMessages[firstUserMessageIndex];
            
            // Check if this message has form context in metadata
            if (firstMsgData?.metadata?.form_context) {
              contextMessage = {
                id: `context-${firstUserMessage.id}`,
                sender: 'context',
                timestamp: firstUserMessage.timestamp,
                formData: {
                  goals: firstMsgData.metadata.form_context.goals || null,
                  challenges: firstMsgData.metadata.form_context.challenges || null,
                  resources: firstMsgData.metadata.form_context.resources || null,
                  description: firstMsgData.metadata.form_context.description || null,
                  relatedFields: firstMsgData.metadata.form_context.relatedFields || []
                }
              };
            }
          }
          
          // Remove any existing context messages to avoid duplicates
          const messagesWithoutContext = mappedMessages.filter(m => m.sender !== 'context');
          
          // Insert context message AFTER first user message (not before)
          if (contextMessage && firstUserMessageIndex !== -1) {
            // Find the index of first user message in the cleaned array
            const firstUserInClean = messagesWithoutContext.findIndex(m => 
              m.sender === 'user' && m.id === mappedMessages[firstUserMessageIndex].id
            );
            if (firstUserInClean !== -1) {
              // Insert context right after first user message
              messagesWithoutContext.splice(firstUserInClean + 1, 0, contextMessage);
            }
          }
          
          if (messagesWithoutContext.length > 0) {
            setMessages(messagesWithoutContext);
          }
        } catch (error) {
          console.error('Error loading messages from thread:', error);
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => prev.filter(m => m.id !== tempUserMessage.id));
      
      let errorText = 'Sorry, I encountered an error while processing your request. Please try again.';
      if (error.name === 'AbortError' || error.message?.includes('aborted')) {
        errorText = 'The request took longer than expected. The AI is still processing your request. Please wait a moment and refresh the page to see the response.';
      } else if (error.message) {
        errorText = `Error: ${error.message}`;
      }
      
      const errorMessage = {
        id: Date.now() + 1,
        text: errorText,
        sender: 'bot',
        timestamp: new Date().toLocaleTimeString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setCurrentThreadId(null);
    setShowForm(true);
    setFormData(null);
    setShowFormContext(false);
  };

  const loadChat = async (threadId) => {
    setCurrentThreadId(threadId);
    setShowForm(false);
    
    try {
      const messagesData = await apiRequest(`/messages/${threadId}`);
      const mappedMessages = messagesData.map((msg, index) => ({
        id: msg.message_id,
        text: msg.content,
        sender: msg.role === 'assistant' ? 'bot' : msg.role,
        timestamp: new Date(msg.created_at).toLocaleTimeString(),
        sources: (msg.metadata?.knowledge_used || []).map(source => ({
          ...source,
          url: source.url || source.source_url || '', 
        }))
      }));
      
      // Check if first user message has form context in metadata
      const firstUserMessageIndex = mappedMessages.findIndex(m => m.sender === 'user');
      
      if (firstUserMessageIndex !== -1) {
        const firstMsgData = messagesData[firstUserMessageIndex];
        
        if (firstMsgData?.metadata?.form_context) {
          const contextMessage = {
            id: `context-${mappedMessages[firstUserMessageIndex].id}`,
            sender: 'context',
            timestamp: mappedMessages[firstUserMessageIndex].timestamp,
            formData: {
              goals: firstMsgData.metadata.form_context.goals || null,
              challenges: firstMsgData.metadata.form_context.challenges || null,
              resources: firstMsgData.metadata.form_context.resources || null,
              description: firstMsgData.metadata.form_context.description || null,
              relatedFields: firstMsgData.metadata.form_context.relatedFields || []
            }
          };
          
          // Insert context message after first user message
          mappedMessages.splice(firstUserMessageIndex + 1, 0, contextMessage);
        }
      }
      
      setMessages(mappedMessages);
    } catch (error) {
      console.error('Error loading chat messages:', error);
      setMessages([]);
    }
  };

  const handleFormSubmit = (data) => {
    const wasEditing = isEditingForm;
    setFormData(data);
    setShowForm(false);
    setIsEditingForm(false);
    
    // If editing and context message exists, update it
    if (wasEditing) {
      setMessages(prev => {
        const contextMsgIndex = prev.findIndex(m => m.sender === 'context');
        if (contextMsgIndex !== -1) {
          const updated = [...prev];
          updated[contextMsgIndex] = {
            ...updated[contextMsgIndex],
            formData: data
          };
          return updated;
        }
        return prev;
      });
    }
    
    console.log('Form submitted with data:', data);
    console.log('Context for RAG:', {
      goals: data.goals,
      challenges: data.challenges,
      resources: data.resources,
      description: data.description,
      relatedFields: data.relatedFields
    });
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setIsEditingForm(false);
  };

  const deleteChat = async (threadId, e) => {
    e.stopPropagation();
    
    // Find the thread to get its title
    const thread = threads.find(t => t.thread_id === threadId);
    if (thread) {
      setThreadToDelete({ id: threadId, title: thread.title });
      setDeleteDialogOpen(true);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!threadToDelete) return;
    
    try {
      await apiRequest(`/threads/${threadToDelete.id}`, { method: 'DELETE' });
      setThreads(prev => prev.filter(thread => thread.thread_id !== threadToDelete.id));
      
      if (currentThreadId === threadToDelete.id) {
        setCurrentThreadId(null);
        setMessages([]);
        setShowForm(true);
        setFormData(null);
      }
      
      setDeleteDialogOpen(false);
      setThreadToDelete(null);
    } catch (error) {
      console.error('Error deleting thread:', error);
      setDeleteDialogOpen(false);
      setThreadToDelete(null);
      // TODO: Show error message to user
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setThreadToDelete(null);
  };

  const handleSourceClick = (source) => {
    if (source.navigation_link) {
      navigate(source.navigation_link);
    }
  };

  const handleOpenDocument = (doc) => {
    console.log('Opening document:', doc);
    // Implement document opening logic
  };

  const handleDownloadDocument = (doc) => {
    console.log('Downloading document:', doc);
    // Implement document download logic
  };

  const renderSourceLinks = (sources) => {
    if (!sources || sources.length === 0) return null;

    const uniqueSourcesMap = {};
    sources.forEach((source) => {
      const url = source.url || source.source_url || '';
      const key = url || `${source.title || ''}-${source.section || ''}`;
      if (!uniqueSourcesMap[key]) {
        uniqueSourcesMap[key] = { ...source, url }; 
      }
    });
    const uniqueSources = Object.values(uniqueSourcesMap);

    return (
      <Box sx={{ mt: 2, pt: 1, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
        <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 'bold' }}>
          📚 Sources:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {uniqueSources.map((source, index) => {
            const sourceUrl = source.url || source.source_url || '';

            let label = '';
            if (source.title && source.title !== 'Untitled' && source.title.trim() !== '') {
              label = source.title;
            } else if (source.category && source.category !== 'N/A' && source.category.trim() !== '') {
              label = source.category;
            } else if (source.region && source.region !== 'N/A' && source.region.trim() !== '') {
              label = source.region;
            } else if (source.text && source.text.trim() !== '') {
              // Use first part of text content as fallback
              label = source.text.substring(0, 50).trim();
              if (source.text.length > 50) label += '...';
            } else if (source.section && source.section.trim() !== '') {
              label = source.section;
            } else {
              label = 'View Source';
            }
            
            if (source.section && source.section !== label && source.section !== source.title) {
              label += ` – ${source.section}`;
            }
            
            if (source.snippet && source.snippet.trim() !== '') {
              label += `: ${source.snippet.substring(0, 40)}...`;
            }
            
            if (sourceUrl) {
              const isRelative = sourceUrl.startsWith('/');
              const href = isRelative ? sourceUrl : sourceUrl;
              
              return (
                <Chip
                  key={index}
                  icon={<ChatBubbleOutlineIcon />}
                  label={label}
                  size="small"
                  clickable
                  component="a"
                  href={href}
                  target={isRelative ? undefined : "_blank"}
                  rel={isRelative ? undefined : "noopener noreferrer"}
                  className={styles.sourceChip}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'secondary.main',
                      color: 'black',
                    }
                  }}
                />
              );
            } else {
              // No URL, just display as non-clickable chip
              return (
                <Chip
                  key={index}
                  icon={<ChatBubbleOutlineIcon />}
                  label={label}
                  size="small"
                  className={styles.sourceChip}
                />
              );
            }
          })}
        </Box>
      </Box>
    );
  };



  return (
    <Box
      sx={{
        height: { xs: '100vh', md: 'calc(100vh - 145px)' },
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        overflow: 'hidden',
        bgcolor: default_theme.palette.primary.bgcolor,
      }}
    >
      {/* LEFT: Chat History */}
      <ChatHistory
        threads={threads}
        currentThreadId={currentThreadId}
        onStartNewChat={startNewChat}
        onLoadChat={loadChat}
        onDeleteChat={deleteChat}
      />

      {/* CENTER: Main Chat Area */}
      <Box sx={{ 
        flex: 1, 
        minWidth: 0,
        display: 'flex', 
        flexDirection: 'column', 
        mx: { xs: 0, md: 1 },
        height: '100%',
      }}>
        <Paper elevation={3} sx={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column', 
          borderRadius: { xs: 0, md: 2 }, 
          overflow: 'hidden',
        }}>
          {/* Chat Header */}
          <Box className={styles.chatHeader} sx={{ 
            p: 2, 
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <Typography variant="h6" className={styles.headerTitle} sx={{ display: 'flex', alignItems: 'center' }}>
              <ChatBubbleOutlineIcon sx={{ mr: 1 }} /> AI Assistant
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={startNewChat}
              size="small"
              sx={{ 
                display: { xs: 'flex', md: 'none' }, 
                bgcolor: '#00b894', 
                '&:hover': { bgcolor: '#00a085' },
                textTransform: 'none',
              }}
            >
              New
            </Button>
          </Box>

          {/* Messages Area */}
          <Box sx={{ 
            flex: 1, 
            overflow: 'auto', 
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            bgcolor: default_theme.palette.primary.bgcolor,
          }}>
            {showForm ? (
              <WizardForm 
                onFormSubmit={handleFormSubmit} 
                onCancel={handleFormCancel}
                initialData={formData}
                isEditing={isEditingForm}
              />
            ) : (
              <Box sx={{ 
                flex: 1,
                p: { xs: 1, md: 2 },
                display: 'flex',
                flexDirection: 'column',
                gap: 2
              }}>
                {messages.length === 0 && (
                  <Box sx={{ 
                    textAlign: 'center', 
                    color: 'text.secondary',
                    py: 4,
                    px: { xs: 1, md: 0 }
                  }}>
                    <Typography variant="body1" gutterBottom>
                      👋 Hello! I'm your AI assistant. I can help you find information about:
                    </Typography>
                    <Typography variant="body2" component="div" sx={{ mt: 2 }}>
                      <ul style={{ textAlign: 'left', display: 'inline-block' }}>
                        <li>Disaster management policies</li>
                        <li>Operational strategies</li>
                        <li>Past experiences</li>
                        <li>Risk management approaches</li>
                      </ul>
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 2 }}>
                      Ask me anything!
                    </Typography>
                    {formData && (
                      <Paper sx={{ 
                        mt: 3, 
                        p: 3, 
                        bgcolor: '#f8f9fa', 
                        textAlign: 'left',
                        border: '2px solid #00b894',
                        borderRadius: 2,
                        maxWidth: '800px',
                        mx: 'auto',
                        width: '100%'
                      }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: '#00b894' }}>
                            📋 Your Project Summary
                          </Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => {
                              setIsEditingForm(true);
                              setShowForm(true);
                            }}
                            sx={{
                              borderColor: '#00b894',
                              color: '#00b894',
                              '&:hover': {
                                borderColor: '#00a085',
                                bgcolor: '#e8f5e8'
                              }
                            }}
                          >
                            Edit
                          </Button>
                        </Box>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: '#666' }}>
                              Goals to Achieve:
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.primary', whiteSpace: 'pre-wrap' }}>
                              {formData.goals || 'Not specified'}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: '#666' }}>
                              Challenges Faced:
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.primary', whiteSpace: 'pre-wrap' }}>
                              {formData.challenges || 'Not specified'}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: '#666' }}>
                              Available Resources:
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.primary', whiteSpace: 'pre-wrap' }}>
                              {formData.resources || 'Not specified'}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: '#666' }}>
                              Project Description:
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.primary', whiteSpace: 'pre-wrap' }}>
                              {formData.description || 'Not specified'}
                            </Typography>
                          </Box>

                          {formData.relatedFields && formData.relatedFields.length > 0 && (
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#666' }}>
                                Related Fields:
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {formData.relatedFields.map((field) => (
                                  <Chip
                                    key={field}
                                    label={field}
                                    size="small"
                                    sx={{
                                      bgcolor: '#e8f5e8',
                                      color: '#00b894',
                                      fontWeight: 500
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Paper>
                    )}
                  </Box>
                )}
                
                {messages.map((message) => {
                  // Render form context message
                  if (message.sender === 'context' && message.formData) {
                    return (
                      <Box
                        key={message.id}
                        sx={{ 
                          mb: 2, 
                          mx: { xs: 1, md: 2 },
                          mt: 1
                        }}
                      >
                        <Paper
                          elevation={2}
                          sx={{
                            p: 2,
                            bgcolor: '#e8f5e8',
                            border: '2px solid #00b894',
                            borderRadius: 2,
                            borderLeftWidth: 4,
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#00b894' }}>
                            📋 Project Context
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {message.formData.goals && (
                              <Box>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', display: 'block', mb: 0.5 }}>
                                  Goals:
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.primary', whiteSpace: 'pre-wrap' }}>
                                  {message.formData.goals}
                                </Typography>
                              </Box>
                            )}
                            {message.formData.challenges && (
                              <Box>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', display: 'block', mb: 0.5 }}>
                                  Challenges:
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.primary', whiteSpace: 'pre-wrap' }}>
                                  {message.formData.challenges}
                                </Typography>
                              </Box>
                            )}
                            {message.formData.resources && (
                              <Box>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', display: 'block', mb: 0.5 }}>
                                  Resources:
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.primary', whiteSpace: 'pre-wrap' }}>
                                  {message.formData.resources}
                                </Typography>
                              </Box>
                            )}
                            {message.formData.description && (
                              <Box>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', display: 'block', mb: 0.5 }}>
                                  Description:
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.primary', whiteSpace: 'pre-wrap' }}>
                                  {message.formData.description}
                                </Typography>
                              </Box>
                            )}
                            {message.formData.relatedFields && message.formData.relatedFields.length > 0 && (
                              <Box>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', display: 'block', mb: 0.5 }}>
                                  Related Fields:
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {message.formData.relatedFields.map((field) => (
                                    <Chip
                                      key={field}
                                      label={field}
                                      size="small"
                                      sx={{
                                        bgcolor: '#ffffff',
                                        color: '#00b894',
                                        fontWeight: 500,
                                        border: '1px solid #00b894'
                                      }}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            )}
                          </Box>
                        </Paper>
                      </Box>
                    );
                  }
                  
                  // Render regular messages
                  return (
                    <Box
                      key={message.id}
                      className={
                        message.sender === 'user'
                          ? styles.userMessage
                          : message.isError
                            ? styles.errorMessage
                            : styles.botMessage
                      }
                      sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
                    >
                      <Avatar sx={{ 
                        bgcolor: default_theme.palette.other.main,
                        width: { xs: 28, md: 32 }, 
                        height: { xs: 28, md: 32 } 
                      }}>
                        {message.sender === 'user' ? <PersonIcon /> : <ChatBubbleOutlineIcon />}
                      </Avatar>
                      <Box className={styles.messageContent} sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {message.text}
                        </Typography>
                        {message.sender === 'bot' && !message.isError && renderSourceLinks(message.sources)}
                        <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.7 }}>
                          {message.timestamp}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
                
                {isLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <Box sx={{
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: 'grey.100',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}>
                      <Avatar sx={{ bgcolor: 'grey.400', width: 28, height: 28 }}>
                        <ChatBubbleOutlineIcon />
                      </Avatar>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {[0, 0.2, 0.4].map((delay, i) => (
                          <Box key={i} sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: 'grey.400',
                            animation: `typing 1.4s infinite ease-in-out ${delay}s`
                          }} />
                        ))}
                      </Box>
                    </Box>
                  </Box>
                )}
                
                <div ref={messagesEndRef} />
              </Box>
            )}
          </Box>

          {/* Input Area */}
          <Box sx={{ 
            p: { xs: 1, md: 2 }, 
            borderTop: '1px solid #e0e0e0',
            display: 'flex',
            gap: 1,
            alignItems: 'flex-end',
            bgcolor: default_theme.palette.primary.bgcolor,
          }}>
            <TextField
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={showForm ? "Please complete the form above to start chatting..." : "Type your message here..."}
              disabled={isLoading || showForm}
              multiline
              minRows={1}
              maxRows={4}
              variant="outlined"
              fullWidth
              InputProps={{
                style: { background: showForm ? '#f5f5f5' : '#fff', borderRadius: 8 },
              }}
            />
            <IconButton
              onClick={sendMessage}
              disabled={!inputMessage.trim() || isLoading || showForm}
              color="primary"
              aria-label="send"
              size="large"
              sx={{ 
                bgcolor: 'grey',
                color: 'white',
                '&:hover': { bgcolor: 'primary.dark' },
                '&:disabled': { bgcolor: 'grey.300' },
              }}
            >
              {isLoading ? '⏳' : <SendIcon />}
            </IconButton>
          </Box>
        </Paper>
      </Box>

      {/* RIGHT: Documents */}
      {/* <DocumentsSidebar
        documents={documents}
        onOpenDocument={handleOpenDocument}
        onDownloadDocument={handleDownloadDocument}
      /> */}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title" sx={{ color: '#d32f2f', fontWeight: 600 }}>
          Delete Chat Thread?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete <strong>"{threadToDelete?.title}"</strong>?
            <br />
            <br />
            This action cannot be undone. All messages and data in this chat will be permanently deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button 
            onClick={handleDeleteCancel} 
            color="inherit"
            sx={{ color: '#666' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained"
            color="error"
            sx={{ 
              bgcolor: '#d32f2f',
              '&:hover': { bgcolor: '#c62828' }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <style jsx>{`
        @keyframes typing {
          0%, 60%, 100% {
            transform: translateY(0);
          }
          30% {
            transform: translateY(-10px);
          }
        }
      `}</style>
    </Box>
  );
};

export default WizardPage;
